import React, { lazy, Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { check_login_bool } from "./ressources/functions/Login";
import {
  loadConfig,
  config as getConfig,
  LT,
} from "./ressources/functions/Config";
import { WebSocketSetup } from "./views/socket";
import { getPillar } from "./views/administration/Pillar";
import { sbToken, sbLogin, sbValid } from "./components/utils/localValues";
import { getSBdriveToken } from "./views/sbdrive/functions/sbdriveLogin";
import { beginImpersonationSession } from "./ressources/functions/helper";

import RouteErrorBoundary from "./components/routes/RouteErrorBoundary";
import "./assets/css/vendor/bootstrap-float-label.min.css";
import "./assets/css/vendor/bootstrap.css";
import "./assets/font/iconsmind-s/css/iconsminds.css";
import "./assets/font/simple-line-icons/css/simple-line-icons.css";
import "rsuite/dist/rsuite.min.css";
import "./index.css";
import "./assets/styles/admin.css";
import "./assets/css/portalplus.css";
import "./assets/css/drive.css";

const DefaultRoutes = lazy(() => import("./components/routes/DefaultRoutes"));
const AppRoutes = lazy(() => import("./components/routes/AppRoutes"));

// Create the Query Client
const queryClient = new QueryClient();

const RootComponent = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [token, setToken] = useState(null);
  const [login, setLogin] = useState(null);
  const [valid, setValid] = useState(null);
  let config = getConfig;

  useEffect(() => {
    const initializeApp = async () => {
      try {
        beginImpersonationSession();
        await loadConfig();

        // If we are landing on a login-related path, try to restore the session
        // BEFORE we snapshot local storage into React state.
        const { pathname } = window.location;
        const isLoginRelatedPath =
          pathname === "/" ||
          pathname === "/logout" ||
          pathname.startsWith("/samllogin");

        const isSsoCallbackError =
          new URLSearchParams(window.location.search).get("sso_error") ===
          "callback";

        if (document.URL.match("/sso?")) {
          // fire and forget
          fetch(document.URL).then((resp) => resp.json());
        }

        // Let the login page apply its bounded callback retry guard. Calling
        // bootstrap auto-login here would otherwise restart SSO before the
        // retry/error UI can be shown.
        if (isLoginRelatedPath && !isSsoCallbackError) {
          await check_login_bool();
        }

        // Snapshot storage into React state after any auto-login.
        setToken(sbToken());
        setLogin(sbLogin());
        setValid(sbValid());
      } catch (err) {
        console.error("App initialization error:", err);
      } finally {
        // Always flip the flag so the UI can render (login page or app).
        setConfigLoaded(true);
      }
    };

    initializeApp();
  }, []);

  // Define isValidState before useEffect
  const isValidState = () => {
    const isTokenValid =
      token &&
      valid !== "false" &&
      valid !== undefined &&
      valid !== "undefined";
    const isLoginValid = login && login.roles !== undefined;
    const isConfigValid = config !== null && config !== undefined;

    return isTokenValid && isLoginValid && isConfigValid;
  };

  useEffect(() => {
    const runPostLoginInit = async () => {
      if (!configLoaded) return;
      if (initialized) return;
      if (!isValidState()) return;

      LT();
      await getPillar();
      getSBdriveToken();
      WebSocketSetup();
      setInitialized(true);
    };

    runPostLoginInit();
  }, [initialized, token, login, valid, configLoaded]);

  if (!configLoaded) {
    return (
      <div className="loading-suspense">
        <span className="dot-pulse"></span>
      </div>
    );
  }

  if (isValidState()) {
    // Don't render app routes until post-login init (including getPillar)
    // has completed. This ensures the module-level `pillars` global is
    // populated before any admin page evaluates and reads from it.
    if (!initialized) {
      return (
        <div className="loading-suspense">
          <span className="dot-pulse"></span>
        </div>
      );
    }
    return (
        <AppRoutes />
    );
  } else {
    return <DefaultRoutes />;
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <RouteErrorBoundary>
        <Suspense
          fallback={
            <div className="loading-suspense">
              <span className="dot-pulse"></span>
            </div>
          }
        >
          <RootComponent />
        </Suspense>
      </RouteErrorBoundary>
    </QueryClientProvider>
  </BrowserRouter>
);
